
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, MessageCircle, ShieldCheck, HelpCircle, Send, ChevronRight, Search, Zap } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useSupportTickets } from '@/shared/hooks/useSupportTickets';
import { useAuth } from '@/shared/hooks/useAuth';

const SupportPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { createTicket } = useSupportTickets();
    const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [type, setType] = useState<'General' | 'Account' | 'Finance' | 'Technical'>('General');
    const [submitting, setSubmitting] = useState(false);

    const faqs = [
        { q: '입금이 지연될 때 어떻게 하나요?', a: '은행 점검 시간 또는 네트워크 혼잡으로 인해 지연될 수 있습니다. 1시간 이상 지연 시 고객센터로 문의 주세요.' },
        { q: '비밀번호를 재설정하고 싶어요.', a: '설정 > 보안 > 비밀번호 변경 메뉴에서 가능합니다. 이메일 인증이 필요할 수 있습니다.' },
        { q: '실명 인증이 거절되었습니다.', a: '신분증 사진의 빛 반사 또는 정보 불일치로 인해 거절될 수 있습니다. 가이드를 참고하여 재시도해 주세요.' },
    ];

    const handleSubmit = async () => {
        if (!subject || !content || !user) {
            alert('로그인 후 제목과 내용을 모두 입력해 주세요.');
            return;
        }

        setSubmitting(true);
        const result = await createTicket({
            userId: user.uid,
            userName: user.displayName || user.email?.split('@')[0] || '익명',
            userEmail: user.email || '',
            subject,
            content,
            type
        });
        setSubmitting(false);

        if (result.success) {
            alert('문의가 성공적으로 접수되었습니다. 담당자 확인 후 답변 드리겠습니다.');
            setIsInquiryModalOpen(false);
            setSubject('');
            setContent('');
        } else {
            alert(`문의 제출 중 오류가 발생했습니다: ${result.error}`);
        }
    };

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl text-left">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-500 transition-colors">
                            <MessageSquare className="h-3.5 w-3.5 fill-emerald-500" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">전담 우선 지원 데스크</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            무엇을 도와드릴까요?<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-500 dark:via-teal-500 dark:to-cyan-500">
                                전담 고객 지원 센터
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            24/7 전문 상담 팀이 대기 중입니다. 이용 중인 서비스에 대해<br />
                            궁금한 점이나 기술적 지원이 필요하시면 언제든 문의하세요.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 min-w-[320px]">
                        <button
                            onClick={() => navigate('/my-inquiries')}
                            className="h-14 px-6 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                        >
                            <MessageSquare className="h-4 w-4" />
                            내 문의내역
                        </button>
                        <button
                            onClick={() => navigate('/chat')}
                            className="h-14 px-6 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                        >
                            <MessageCircle className="h-4 w-4" />
                            실시간 채팅
                        </button>
                        <div className="relative group flex-1">
                            <Search className="absolute left-6 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50 group-focus-within:text-emerald-500 transition-colors" />
                            <input
                                className="h-14 w-full bg-card border border-border rounded-2xl pl-14 pr-6 text-xs font-black outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/5 transition-all placeholder:text-muted-foreground/30 uppercase tracking-widest"
                                placeholder="도움말 또는 주제 검색..."
                            />
                        </div>
                    </div>
                </div>

                {/* Contact Methods Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { icon: MessageSquare, title: '1:1 문의기능', desc: '월-금 09:00 - 18:00', color: 'bg-emerald-500', trend: '순차적 답변 처리', action: () => setIsInquiryModalOpen(true) },
                        { icon: MessageCircle, title: '시그널 문의', desc: '@cointocash_support', color: 'bg-indigo-500', trend: '24시간 보안 상담', action: () => window.open('https://signal.me/#p/+821012345678', '_blank') },
                        { icon: Send, title: '텔레그램 상담', desc: '@cointocash_official', color: 'bg-blue-500', trend: '실시간 24/7 상담', action: () => window.open('https://t.me/cointocash_official', '_blank') },
                    ].map((item) => (
                        <div key={item.title} onClick={item.action} className="group relative p-8 rounded-[2.5rem] bg-card border border-border hover:border-emerald-500/30 transition-all duration-500 cursor-pointer overflow-hidden shadow-xl hover:shadow-2xl">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/[0.02] blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>

                            <div className={cn(
                                "w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-8 shadow-xl transition-all duration-700 group-hover:scale-110 group-hover:rotate-3",
                                item.color
                            )}>
                                <item.icon className="h-7 w-7" />
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="text-lg font-black text-foreground group-hover:text-emerald-500 transition-colors uppercase tracking-tight">{item.title}</h3>
                                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic mt-1 opacity-60">{item.desc}</p>
                                </div>
                                <div className="pt-4 flex items-center gap-2 text-[8px] font-black text-emerald-500 uppercase tracking-[0.2em] border-t border-border group-hover:border-emerald-500/20 transition-colors">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                                    {item.trend}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* FAQ Section */}
                <div className="space-y-8 pt-8">
                    <div className="flex items-center justify-between px-4">
                        <div className="flex items-center gap-3">
                            <HelpCircle className="h-5 w-5 text-emerald-500" />
                            <h2 className="text-xl font-black text-foreground uppercase tracking-tighter">자주 묻는 질문 (FAQ)</h2>
                        </div>
                        <button className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.3em] italic hover:underline">전체 가이드북 보기</button>
                    </div>

                    <div className="grid gap-4">
                        {faqs.map((faq, i) => (
                            <div key={i} className="group relative p-6 rounded-[2rem] bg-card border border-border hover:border-emerald-500/20 transition-all duration-300 cursor-pointer">
                                <div className="flex items-center justify-between gap-6">
                                    <div className="flex items-center gap-6 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center text-muted-foreground group-hover:bg-emerald-500/10 group-hover:text-emerald-500 transition-all">
                                            <span className="text-[10px] font-black italic">Q{i + 1}</span>
                                        </div>
                                        <span className="font-black text-foreground text-sm group-hover:text-emerald-600 transition-colors leading-relaxed truncate">{faq.q}</span>
                                    </div>
                                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent flex items-center justify-center text-muted-foreground group-hover:text-foreground transition-all">
                                        <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Safety Action Card */}
                <div className="relative group p-12 rounded-[3.5rem] bg-gradient-to-br from-emerald-600 to-teal-700 dark:from-emerald-500 dark:to-teal-600 border border-white/10 flex flex-col md:flex-row items-center gap-12 shadow-2xl overflow-hidden transition-all duration-1000">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 blur-3xl -mr-32 -mt-32 rounded-full group-hover:bg-white/20 transition-colors duration-1000"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 blur-3xl -ml-32 -mb-32 rounded-full"></div>

                    <div className="w-24 h-24 rounded-[2rem] bg-white/20 backdrop-blur-xl flex items-center justify-center border border-white/20 shadow-2xl group-hover:scale-110 transition-transform duration-700 shrink-0">
                        <ShieldCheck className="h-10 w-10 text-white fill-white/10" />
                    </div>

                    <div className="flex-1 text-center md:text-left space-y-4">
                        <div className="space-y-1">
                            <h4 className="text-2xl font-black text-white uppercase tracking-tighter leading-none">보안 강화 프로토콜</h4>
                            <p className="text-white/60 font-bold text-xs uppercase tracking-tight italic">자산 보호를 위한 군사급 보안 지침</p>
                        </div>
                        <p className="text-sm font-bold text-white/80 leading-relaxed max-w-xl">
                            비밀번호 관리 방침 및 피싱 사이트 주의 사항을 확인하여 자산을 안전하게 보호하세요.<br />
                            코인투캐쉬은 어떠한 경우에도 고객님의 비밀번호나 복구 구문을 요구하지 않습니다.
                        </p>
                    </div>

                    <button className="relative overflow-hidden group/btn px-10 h-16 rounded-2xl bg-white text-emerald-950 font-black text-[11px] uppercase tracking-[0.2em] italic shadow-2xl transition-all hover:scale-105 active:scale-95 shrink-0">
                        <span className="relative z-10">보안 가이드 보기</span>
                        <div className="absolute inset-0 bg-emerald-500 opacity-0 group-hover/btn:opacity-10 transition-opacity"></div>
                    </button>
                </div>

                {/* Bottom Assurance */}
                <div className="flex flex-col items-center gap-4 py-12 opacity-30">
                    <p className="text-[9px] font-black uppercase tracking-[0.6em]">검증된 암호화 표준 • Tier 4 규정 준수</p>
                </div>
            </div>

            {/* Inquiry Modal */}
            {isInquiryModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-xl animate-in fade-in duration-500">
                    <div className="w-full max-w-xl bg-card border border-border rounded-[3rem] p-10 space-y-8 shadow-2xl relative overflow-hidden transition-all animate-in zoom-in-95 duration-500 text-left">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/[0.05] blur-3xl rounded-full -mr-32 -mt-32"></div>

                        <div className="flex justify-between items-center relative z-10">
                            <div className="space-y-1">
                                <h2 className="text-3xl font-black text-foreground uppercase tracking-tighter leading-none">1:1 문의하기</h2>
                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em] italic">Personal Priority Support Ticket</p>
                            </div>
                            <button onClick={() => setIsInquiryModalOpen(false)} className="w-10 h-10 rounded-full bg-accent border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all">
                                <span className="font-black text-xs">×</span>
                            </button>
                        </div>

                        <div className="space-y-6 relative z-10">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic px-1">문의 유형</label>
                                <select
                                    value={type}
                                    onChange={(e) => setType(e.target.value as 'General' | 'Account' | 'Finance' | 'Technical')}
                                    className="w-full h-14 bg-accent/30 border border-border rounded-xl px-4 text-xs font-black outline-none focus:border-emerald-500/50"
                                >
                                    <option value="General">일반 문의</option>
                                    <option value="Account">계정/인증</option>
                                    <option value="Finance">입출금/거래</option>
                                    <option value="Technical">기술 오류</option>
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic px-1">문의 제목</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="상담 내용을 요약해 주세요"
                                    className="w-full h-14 bg-accent/30 border border-border rounded-xl px-4 text-xs font-black outline-none focus:border-emerald-500/50"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic px-1">상세 내용</label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="정확한 상담을 위해 최대한 자세히 적어주세요"
                                    rows={5}
                                    className="w-full bg-accent/30 border border-border rounded-xl p-4 text-xs font-black outline-none focus:border-emerald-500/50 resize-none"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="w-full h-16 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-3 group/btn disabled:opacity-50"
                        >
                            <Zap className="h-4 w-4 fill-white group-hover/btn:scale-125 transition-transform" />
                            <span>{submitting ? '제출 중...' : '문의 제출하기'}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupportPage;
